import React, { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import leafletCss from "leaflet/dist/leaflet.css?raw"; // will be used if bundler supports ?raw
import { logger } from "../../shared/logger";

/**
 * MapView (Leaflet)
 * items: [{ id, lat, lng, title, image, guests, bedrooms, bathrooms, href }]
 */
export default function MapView({
  items = [],
  height = 520,
  className = "",
  style = {},
  onPinClick,
  onBoundsChange,
  activeId,
  iconSpec = {
    mode: "fa",
    faFamily: "fa-solid",
    faIcon: "fa-location-dot",
    size: 28,
    color: "#fe5922",
    svg: "",
  },
  autoLoadFontAwesome = true,
  visible = true,
  enableClustering = false,
  clusterStyle = {
    bgColor: "#fe5922",
    textColor: "#ffffff",
    borderColor: "#ffffff",
    borderWidth: 3,
    fontSize: 14,
    fontWeight: 700,
    size: 40,
  },
  /** Optional override for tile style ("default" | "light" | "dark" | "satellite" | "streets") */
  tileStyleProp,
}) {
  /* ---------- Ensure Leaflet CSS exists (supports bundlers & WordPress embeds) ---------- */
  useEffect(() => {
    // prefer inline CSS from ?raw when bundler supports it
    try {
      if (!document.getElementById("__leaflet_css") && leafletCss) {
        const s = document.createElement("style");
        s.id = "__leaflet_css";
        s.textContent = leafletCss;
        document.head.appendChild(s);
        return;
      }
    } catch {
      /* fall through to CDN link below */
    }
    // fallback to CDN link (safe for WP/Elementor)
    if (!document.getElementById("__leaflet_css_link")) {
      const ln = document.createElement("link");
      ln.id = "__leaflet_css_link";
      ln.rel = "stylesheet";
      ln.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      ln.integrity = "sha512-pHb5r5oKQb7f1xgAq2w8VY3Qf2m4Yy3m2j1f6c6mVxXwU5A2m8RbxJ8HkZk2e5mS0eQ3a2Gm/0j8n1DGe+3aBA==";
      ln.crossOrigin = "";
      document.head.appendChild(ln);
    }
  }, []);

  /* ---------- Optionally inject Font Awesome ---------- */
  useEffect(() => {
    if (!autoLoadFontAwesome) return;
    const hasFA =
      !!document.querySelector(
        'link[href*="font-awesome"],link[href*="fontawesome"],link[href*="cdnjs.cloudflare.com/ajax/libs/font-awesome"]'
      ) || !!document.querySelector('link[href*="use.fontawesome.com"]');
    if (!hasFA && !document.getElementById("__fa_css_autoload")) {
      const ln = document.createElement("link");
      ln.id = "__fa_css_autoload";
      ln.rel = "stylesheet";
      ln.href =
        "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.2/css/all.min.css";
      ln.crossOrigin = "anonymous";
      ln.referrerPolicy = "no-referrer";
      document.head.appendChild(ln);
    }
  }, [autoLoadFontAwesome]);

  /* ---------- Refs ---------- */
  const mapRef = useRef(null);
  const tileLayerRef = useRef(null);
  const mapEl = useRef(null);
  const boxRef = useRef(null);
  const markerClusterRef = useRef(null);
  const boundsSet = useRef(false); // Track if initial bounds have been set
  const prevTileStyle = useRef(null); // Track tile style to reset bounds on change
  const [clusteringReady, setClusteringReady] = useState(false);

  /* ---------- Load Leaflet MarkerCluster plugin ---------- */
  useEffect(() => {
    if (!enableClustering) {
      setClusteringReady(false);
      return;
    }

    // Check if already loaded
    if (window.L && window.L.markerClusterGroup) {
      setClusteringReady(true);
      return;
    }

    // Load CSS
    if (!document.getElementById("__leaflet_cluster_css")) {
      const link = document.createElement("link");
      link.id = "__leaflet_cluster_css";
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.css";
      document.head.appendChild(link);

      const link2 = document.createElement("link");
      link2.id = "__leaflet_cluster_css_default";
      link2.rel = "stylesheet";
      link2.href = "https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.Default.css";
      document.head.appendChild(link2);
    }

    // Load JS
    if (!document.getElementById("__leaflet_cluster_js")) {
      const script = document.createElement("script");
      script.id = "__leaflet_cluster_js";
      script.src = "https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js";
      script.onload = () => setClusteringReady(true);
      script.onerror = () => {
        logger.warn("[MapView] Failed to load MarkerCluster plugin");
        setClusteringReady(false);
      };
      document.head.appendChild(script);
    }
  }, [enableClustering]);

  /* ---------- Find widget root (covers embedded shells) ---------- */
  const getRootEl = () => {
    const el = mapEl.current;
    if (!el) return null;
    return (
      el.closest(
        "#nexus-grid-widget, .nexus-grid-widget, [data-nexus-grid], .nxw-listgrid-wrap"
      ) ||
      document.querySelector("[data-map-tile-style]") ||
      el
    );
  };

  /* ---------- Read attributes from root (with legacy mappings) ---------- */
  const readCfgFromRoot = () => {
    const root = getRootEl();
    const get = (name, fallback) => root?.getAttribute(name) ?? fallback;
    const normalize = (s) => String(s || "").trim().toLowerCase();

    // legacy -> new mapping
    const mapLegacy = {
      osm: "default",
      openstreetmap: "default",
      "carto-light": "carto-light",  // keep explicit
      "carto-dark": "carto-dark",    // keep explicit
      esri: "streets",
      "stamen-toner": "default",
      "stamen-watercolor": "default",
    };


    const styleRaw = tileStyleProp ?? get("data-map-tile-style", "default");
    const s = normalize(styleRaw);
    const style = mapLegacy[s] || s;

    const lat = Number(get("data-map-center-lat", 51.5072));
    const lng = Number(get("data-map-center-lng", -0.1276));
    const zoom = Number(get("data-map-zoom", 10));
    const wheel = get("data-map-wheel", "true") !== "false";

    return { lat, lng, zoom, wheel, style };
  };

  /* ---------- Local reactive tile style ---------- */
  const [tileStyle, setTileStyle] = useState(
    (tileStyleProp && String(tileStyleProp).toLowerCase()) || "default"
  );

  /* ---------- Reset boundsSet when tile style prop changes (e.g., switching to satellite) ---------- */
  useEffect(() => {
    const currentStyle = tileStyleProp ? String(tileStyleProp).toLowerCase() : "default";
    if (prevTileStyle.current !== null && prevTileStyle.current !== currentStyle) {
      // Tile style changed - reset bounds so we fit to markers again
      logger.log("[MapView] Tile style changed from", prevTileStyle.current, "to", currentStyle, "- resetting bounds");
      boundsSet.current = false;
    }
    prevTileStyle.current = currentStyle;
  }, [tileStyleProp]);

  /* ---------- Keep config in sync with attributes / prop ---------- */
  useEffect(() => {
    const root = getRootEl();
    if (!root) return;

    const applyCfgToMap = (cfg) => {
      const m = mapRef.current;
      if (!m) return;
      try {
        if (Number.isFinite(cfg.lat) && Number.isFinite(cfg.lng)) {
          m.setView([cfg.lat, cfg.lng], cfg.zoom, { animate: false });
        }
        m.scrollWheelZoom[cfg.wheel ? "enable" : "disable"]?.();
      } catch {}
    };

    const first = readCfgFromRoot();
    setTileStyle(first.style);
    applyCfgToMap(first);

    // delayed one more time (for late-arriving attrs)
    const t = setTimeout(() => {
      const again = readCfgFromRoot();
      setTileStyle((old) => (old !== again.style ? again.style : old));
      applyCfgToMap(again);
    }, 60);

    const obs = new MutationObserver(() => {
      const cfg = readCfgFromRoot();
      setTileStyle(cfg.style);
      applyCfgToMap(cfg);
    });
    obs.observe(root, { attributes: true });

    return () => {
      clearTimeout(t);
      obs.disconnect();
    };
  }, [tileStyleProp]);

  /* ---------- Marker icon ---------- */
  const pinIcon = useMemo(() => {
    const size = Number.isFinite(iconSpec?.size) ? iconSpec.size : 28;
    const color = iconSpec?.color || "#fe5922";
    const iconW = size;
    const iconH = Math.round(size * 1.4);
    const anchor = [Math.round(iconW / 2), iconH];
    const popupAnchor = [0, -Math.max(10, Math.round(iconH * 0.9))];

    if (iconSpec?.mode === "svg" && iconSpec?.svg) {
      const cleaned = String(iconSpec.svg)
        .replace(/<script[\s\S]*?<\/script>/gi, "")
        .replace(/\son\w+="[^"]*"/gi, "")
        .replace(/fill="(?!none)[^"]*"/gi, 'fill="currentColor"');
      const sized = cleaned.replace(
        /<svg\b([^>]*)>/i,
        `<svg $1 width="${iconW}" height="${iconH}" preserveAspectRatio="xMidYMid meet">`
      );
      const dataUrl =
        "data:image/svg+xml;charset=utf-8," + encodeURIComponent(sized);
      return L.icon({
        iconUrl: dataUrl,
        iconSize: [iconW, iconH],
        iconAnchor: anchor,
        popupAnchor,
        className: "nexus-pin nexus-pin--svg",
      });
    }

    const faFamily = iconSpec?.faFamily || "fa-solid";
    const faIcon = iconSpec?.faIcon || "fa-location-dot";
    const html = `<i class="${faFamily} ${faIcon}" style="font-size:${size}px;color:${color};display:inline-block;line-height:1;"></i>`;

    return L.divIcon({
      className: "nexus-pin nexus-pin--fa",
      html,
      iconSize: [iconW, iconH],
      iconAnchor: anchor,
      popupAnchor,
    });
  }, [iconSpec]);

  /* ---------- Initialize map ---------- */
  useEffect(() => {
    if (!visible) return;
    if (mapRef.current || !mapEl.current) return;

    const cfg = readCfgFromRoot();
    const el = mapEl.current;

    try {
      const map = L.map(el, {
        center: [cfg.lat, cfg.lng],
        zoom: cfg.zoom,
        scrollWheelZoom: cfg.wheel,
      });

      const layer = L.tileLayer(
        getTileUrl(cfg.style),
        getTileOptions(cfg.style)
      ).addTo(map);

      map.__markerById = new Map();
      mapRef.current = map;
      tileLayerRef.current = layer;

      const ro = new ResizeObserver(() => {
        try {
          if (mapRef.current) map.invalidateSize();
        } catch (e) {
          // Ignore leaflet errors during resize
        }
      });
      ro.observe(boxRef.current || el);
      setTimeout(() => {
        try {
          if (mapRef.current) map.invalidateSize();
        } catch (e) {
          // Ignore
        }
      }, 200);
    } catch (e) {
      console.error("[MapView] Failed to init Leaflet map:", e);
    }

    return () => {
      try {
        if (mapRef.current) {
          mapRef.current.remove();
          mapRef.current = null;
        }
        tileLayerRef.current = null;
      } catch {}
    };
  }, [visible]);

  /* ---------- Listen for map bounds changes (pan/zoom) ---------- */
  useEffect(() => {
    const m = mapRef.current;
    if (!m || !onBoundsChange) return;

    const emitBounds = () => {
      try {
        const bounds = m.getBounds();
        if (bounds) {
          const ne = bounds.getNorthEast();
          const sw = bounds.getSouthWest();
          onBoundsChange({
            north: ne.lat,
            south: sw.lat,
            east: ne.lng,
            west: sw.lng,
          });
        }
      } catch (e) {
        logger.warn("[MapView] Failed to get bounds:", e);
      }
    };

    // Emit initial bounds after a short delay (map needs to be ready)
    const initTimer = setTimeout(emitBounds, 500);

    // Listen for user pan/zoom
    m.on("moveend", emitBounds);
    m.on("zoomend", emitBounds);

    return () => {
      clearTimeout(initTimer);
      m.off("moveend", emitBounds);
      m.off("zoomend", emitBounds);
    };
  }, [onBoundsChange]);

  /* ---------- Switch tile layer when style changes ---------- */
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;

    try {
      const old = tileLayerRef.current;
      if (old) m.removeLayer(old);

      const url = getTileUrl(tileStyle);
      const opts = getTileOptions(tileStyle);
      const layer = L.tileLayer(url, opts).addTo(m);
      tileLayerRef.current = layer;

      let errorCount = 0;
      const onErr = () => {
        errorCount++;
        if (errorCount >= 5) {
          logger.warn("[MapView] tile load failed, switching to default OSM");
          setTileStyle("default");
        }
      };
      layer.on("tileerror", onErr);

      requestAnimationFrame(() => {
        m.invalidateSize();

        // After switching tile styles, re-fit bounds to markers if we have them
        if (m.__markers && m.__markers.length > 0) {
          try {
            const bounds = L.latLngBounds([]);
            m.__markers.forEach(mk => {
              const ll = mk.getLatLng();
              if (ll) bounds.extend(ll);
            });
            if (bounds.isValid()) {
              logger.log("[MapView] Re-fitting bounds after tile style change");
              m.fitBounds(bounds, { padding: [40, 40] });
            }
          } catch (e) {
            logger.warn("[MapView] Failed to refit bounds:", e);
          }
        }
      });

      return () => layer.off("tileerror", onErr);
    } catch (e) {
      logger.warn("[MapView] Failed switching tiles:", e);
    }
  }, [tileStyle]);

  /* ---------- Place markers ---------- */
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;

    // Wait for clustering library if needed
    if (enableClustering && !clusteringReady) return;

    // clear previous
    try {
      if (markerClusterRef.current) {
        m.removeLayer(markerClusterRef.current);
        markerClusterRef.current = null;
      }
      if (m.__markers) m.__markers.forEach((mk) => mk.remove());
      m.__markers = [];
      m.__markerById?.clear?.();
    } catch {}

    const pts = (items || []).filter(
      (it) => Number.isFinite(it?.lat) && Number.isFinite(it?.lng)
    );
    if (!pts.length) return;

    // Create marker cluster group if clustering is enabled
    let clusterGroup = null;
    if (enableClustering && window.L && window.L.markerClusterGroup) {
      clusterGroup = window.L.markerClusterGroup({
        showCoverageOnHover: false,
        zoomToBoundsOnClick: true,
        spiderfyOnMaxZoom: true,
        removeOutsideVisibleBounds: true,
        animate: true,
        maxClusterRadius: 80,
        iconCreateFunction: function(cluster) {
          const count = cluster.getChildCount();
          const size = clusterStyle.size || 40;
          const bgColor = clusterStyle.bgColor || "#fe5922";
          const textColor = clusterStyle.textColor || "#ffffff";
          const borderColor = clusterStyle.borderColor || "#ffffff";
          const borderWidth = clusterStyle.borderWidth || 3;
          const fontSize = clusterStyle.fontSize || 14;
          const fontWeight = clusterStyle.fontWeight || 700;

          return L.divIcon({
            html: `<div style="
              width: ${size}px;
              height: ${size}px;
              background-color: ${bgColor};
              border: ${borderWidth}px solid ${borderColor};
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              color: ${textColor};
              font-size: ${fontSize}px;
              font-weight: ${fontWeight};
              font-family: system-ui, -apple-system, sans-serif;
              box-shadow: 0 2px 8px rgba(0,0,0,0.2);
            ">${count}</div>`,
            className: 'nexus-cluster-icon',
            iconSize: L.point(size, size),
          });
        },
      });
      markerClusterRef.current = clusterGroup;
    }

    const bounds = L.latLngBounds([]);
    const markers = [];

    pts.forEach((it, idx) => {
      const id = it?.id ?? idx;
      const mk = L.marker([it.lat, it.lng], { icon: pinIcon });
      mk.bindPopup(buildCardHtml(it), {
        className: "nexus-popup",
        closeButton: true,
        autoClose: true,
        autoPan: true,
        maxWidth: 360,
        minWidth: 280,
        offset: [0, -10],
      });
      mk.on("click", () => onPinClick?.(id, it));

      markers.push(mk);
      (m.__markers || (m.__markers = [])).push(mk);
      m.__markerById?.set?.(id, mk);
      bounds.extend([it.lat, it.lng]);
    });

    // Add markers to cluster or directly to map
    if (clusterGroup) {
      clusterGroup.addLayers(markers);
      m.addLayer(clusterGroup);
    } else {
      markers.forEach(mk => mk.addTo(m));
    }

    // Fit bounds on first load or when switching tile styles
    if (bounds.isValid()) {
      if (!boundsSet.current) {
        try {
          logger.log("[MapView] Initial fitBounds to", pts.length, "markers");
          m.fitBounds(bounds, { padding: [40, 40] });
          boundsSet.current = true;
        } catch {}
      }

      // Also fit bounds with a small delay to handle timing issues (e.g., switching from GoogleMaps to Leaflet)
      setTimeout(() => {
        const currentMarkers = m.__markers || [];
        if (currentMarkers.length > 0 && bounds.isValid()) {
          try {
            // Check if map is still at default center (London or similar far-off location)
            const center = m.getCenter();
            const boundsCenter = bounds.getCenter();
            const distance = center.distanceTo(boundsCenter);
            // If center is more than 100km from markers, we need to refit
            if (distance > 100000) {
              logger.log("[MapView] Map center far from markers, refitting bounds");
              m.fitBounds(bounds, { padding: [40, 40] });
            }
          } catch {}
        }
      }, 300);
    }
  }, [items, pinIcon, onPinClick, enableClustering, clusteringReady, clusterStyle, tileStyle]);

  /* ---------- Focus marker ---------- */
  useEffect(() => {
    const m = mapRef.current;
    if (!m || activeId == null) {
      try {
        m?.closePopup();
      } catch {}
      return;
    }
    const mk = m.__markerById?.get?.(activeId);
    if (!mk) return;
    try {
      mk.openPopup();
      const ll = mk.getLatLng?.();
      if (ll && Number.isFinite(ll.lat) && Number.isFinite(ll.lng)) {
        m.panTo(ll, { animate: true, duration: 0.6 });
      }
    } catch {}
  }, [activeId]);

  /* ---------- Minimal local CSS (popups, wrapper) ---------- */
  useEffect(() => {
    if (document.getElementById("__nxm_map_css")) return;
    const s = document.createElement("style");
    s.id = "__nxm_map_css";
    s.textContent = `
.leaflet-top.leaflet-left { z-index:10!important; }
.nxm-wrap{position:relative;height:var(--nxm-height,520px);width:100%;overflow:hidden;border-radius:12px;border:1px solid rgba(17,24,39,.12);background:transparent;}
.nxm-map{height:100%;width:100%;background:transparent;}
.nexus-popup .leaflet-popup-content-wrapper{padding:0;border-radius:12px;box-shadow:0 14px 34px rgba(2,6,23,.28);border:1px solid rgba(17,24,39,.12);overflow:hidden;}
.nexus-popup .leaflet-popup-content{margin:0!important;}
.nexus-popup .leaflet-popup-tip{display:none;}
.nexus-popup .leaflet-popup-close-button{top:8px;right:8px;width:32px;height:32px;line-height:30px;border-radius:10px;border:1px solid rgba(17,24,39,.12);background:rgba(255,255,255,.85);color:#111827;text-align:center;}
.nx-card{width:340px;max-width:85vw;font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#111827;background:#fff;}
.nx-card-img{display:block;width:100%;aspect-ratio:16/9;object-fit:cover;background:#f3f4f6;}
.nx-card-body{padding:12px 14px 14px;}
.nx-card-title{font-weight:800;font-size:18px;margin:0 0 6px;line-height:1.2;}
.nx-card-meta{font-size:12px;letter-spacing:.2px;color:#6b7280;display:flex;gap:8px;flex-wrap:wrap;}
.nx-dot::before{content:"·";margin:0 6px;color:#9ca3af;}
.nx-card a{color:inherit;text-decoration:none;}
.nx-card a:hover .nx-card-title{text-decoration:underline;}
`;
    document.head.appendChild(s);
  }, []);

  /* ---------- Render ---------- */
  const heightValue = typeof height === 'number' ? `${height}px` : height;
  return (
    <div
      ref={boxRef}
      className={`nxm-wrap ${className || ""}`}
      style={{ "--nxm-height": heightValue, height: heightValue, minHeight: heightValue, ...style }}
      aria-label="Map"
    >
      <div ref={mapEl} className="nxm-map" style={{ height: '100%', minHeight: heightValue }} />
    </div>
  );
}

/* ---------------- helpers ---------------- */
function buildCardHtml(it) {
  const t = escHtml(it?.title || "");
  const href = it?.href ? escAttr(it.href) : "";
  const img = it?.image ? escAttr(it.image) : "";

  const metaBits = [];
  if (Number.isFinite(it?.guests))
    metaBits.push(plural(it.guests, "Guest", "Guests"));
  if (Number.isFinite(it?.bedrooms))
    metaBits.push(plural(it.bedrooms, "Bedroom", "Bedrooms"));
  if (Number.isFinite(it?.bathrooms))
    metaBits.push(plural(it.bathrooms, "Bathroom", "Bathrooms"));
  const meta = metaBits.join(' <span class="nx-dot"></span> ');

  const inner = `
    <div class="nx-card">
      ${
        img
          ? `<img class="nx-card-img" src="${img}" alt="${t}"/>`
          : `<div class="nx-card-img" role="presentation"></div>`
      }
      <div class="nx-card-body">
        <h3 class="nx-card-title">${t}</h3>
        <div class="nx-card-meta">${meta}</div>
      </div>
    </div>
  `;
  return href
    ? `<a href="${href}" target="_blank" rel="noopener noreferrer">${inner}</a>`
    : inner;
}

function plural(n, one, many) {
  return `${n} ${n === 1 ? one : many}`;
}

function escHtml(s = "") {
  return String(s)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function escAttr(s = "") {
  return String(s).replace(/"/g, "&quot;").replace(/'/g, "&#039;");
}

function getTileUrl(style) {
  switch ((style || "default").toLowerCase()) {
    case "carto-dark":
    case "dark":
      return "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png";
    case "carto-light":
    case "light":
      return "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";
    case "satellite":
      return "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
    case "streets":
      return "https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png";
    case "default":
    default:
      return "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
  }
}

function getTileAttribution(style) {
  switch ((style || "default").toLowerCase()) {
    case "carto-dark":
    case "dark":
      return "© OpenStreetMap contributors, © CartoDB DarkMatter";
    case "carto-light":
    case "light":
      return "© OpenStreetMap contributors, © CartoDB Positron";
    case "satellite":
      return "Tiles © Esri — Source: Esri";
    case "streets":
      return "© OpenStreetMap France contributors";
    case "default":
    default:
      return "© OpenStreetMap contributors";
  }
}

function getTileOptions(style) {
  switch ((style || "default").toLowerCase()) {
    case "carto-dark":
    case "dark":
    case "carto-light":
    case "light":
      return {
        maxZoom: 20,
        attribution: getTileAttribution(style),
        crossOrigin: true,
        subdomains: "abcd",
      };
    case "satellite":
      return {
        maxZoom: 20,
        attribution: getTileAttribution(style),
        crossOrigin: true,
      };
    case "streets":
      return {
        maxZoom: 20,
        attribution: getTileAttribution(style),
        crossOrigin: true,
        subdomains: "abc",
      };
    case "default":
    default:
      return {
        maxZoom: 19,
        attribution: getTileAttribution(style),
        crossOrigin: true,
        subdomains: "abc",
      };
  }
}

